// ============================================================================
// SAMPLE GL ACCOUNT SERVICE (Reference Implementation)
// ============================================================================
// Business logic and API calls for General Ledger Accounts module

import { get, post, put, del } from '../client/axiosClient';
import { sampleGLAccountEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  SampleGLAccount,
  CreateSampleGLAccountRequest,
  UpdateSampleGLAccountRequest,
  SampleGLAccountListItem,
  SampleGLAccountFilterParams,
  SampleGLAccountBalance,
  SampleGLAccountHierarchy,
  SampleChartOfAccounts,
} from '../types';

/**
 * GL Account Service Class
 */
class SampleGLAccountService {
  /**
   * Get all GL accounts
   */
  async getAll(params?: SampleGLAccountFilterParams): Promise<ListResponse<SampleGLAccountListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(sampleGLAccountEndpoints.list(), params)
      : sampleGLAccountEndpoints.list();

    const response = await get<ListResponse<SampleGLAccountListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get GL account by ID
   */
  async getById(id: ID): Promise<ApiResponse<SampleGLAccount>> {
    const response = await get<ApiResponse<SampleGLAccount>>(sampleGLAccountEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new GL account
   */
  async create(data: CreateSampleGLAccountRequest): Promise<ApiResponse<SampleGLAccount>> {
    const response = await post<ApiResponse<SampleGLAccount>>(
      sampleGLAccountEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update GL account
   */
  async update(id: ID, data: UpdateSampleGLAccountRequest): Promise<ApiResponse<SampleGLAccount>> {
    const response = await put<ApiResponse<SampleGLAccount>>(
      sampleGLAccountEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete GL account
   */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(sampleGLAccountEndpoints.delete(id));
    return response.data;
  }

  /**
   * Search GL accounts
   */
  async search(params: SampleGLAccountFilterParams): Promise<ListResponse<SampleGLAccountListItem>> {
    const response = await post<ListResponse<SampleGLAccountListItem>>(
      sampleGLAccountEndpoints.search(),
      params
    );
    return response.data;
  }

  /**
   * Get GL accounts by category
   */
  async getByCategory(category: string): Promise<ListResponse<SampleGLAccountListItem>> {
    const response = await get<ListResponse<SampleGLAccountListItem>>(
      sampleGLAccountEndpoints.byCategory(category)
    );
    return response.data;
  }

  /**
   * Get chart of accounts
   */
  async getChart(): Promise<ApiResponse<SampleChartOfAccounts>> {
    const response = await get<ApiResponse<SampleChartOfAccounts>>(
      sampleGLAccountEndpoints.chart()
    );
    return response.data;
  }

  /**
   * Get GL account hierarchy
   */
  async getHierarchy(): Promise<ApiResponse<SampleGLAccountHierarchy[]>> {
    const response = await get<ApiResponse<SampleGLAccountHierarchy[]>>(
      sampleGLAccountEndpoints.hierarchy()
    );
    return response.data;
  }

  /**
   * Get GL account balance
   */
  async getBalance(id: ID): Promise<ApiResponse<SampleGLAccountBalance>> {
    const response = await get<ApiResponse<SampleGLAccountBalance>>(
      sampleGLAccountEndpoints.balance(id)
    );
    return response.data;
  }

  /**
   * Get GL account children
   */
  async getChildren(id: ID): Promise<ListResponse<SampleGLAccountListItem>> {
    const response = await get<ListResponse<SampleGLAccountListItem>>(
      sampleGLAccountEndpoints.children(id)
    );
    return response.data;
  }

  /**
   * Get active GL accounts
   */
  async getActive(): Promise<ListResponse<SampleGLAccountListItem>> {
    const response = await get<ListResponse<SampleGLAccountListItem>>(
      sampleGLAccountEndpoints.active()
    );
    return response.data;
  }

  /**
   * Get posting-enabled GL accounts
   */
  async getPostingEnabled(): Promise<ListResponse<SampleGLAccountListItem>> {
    const response = await get<ListResponse<SampleGLAccountListItem>>(
      sampleGLAccountEndpoints.postingEnabled()
    );
    return response.data;
  }

  /**
   * Bulk delete GL accounts
   */
  async bulkDelete(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(
      sampleGLAccountEndpoints.bulkDelete(),
      {
        data: { ids },
      }
    );
    return response.data;
  }

  /**
   * Export GL accounts
   */
  async export(filters?: SampleGLAccountFilterParams): Promise<Blob> {
    const endpoint = filters
      ? buildEndpointWithQuery(sampleGLAccountEndpoints.export(), filters)
      : sampleGLAccountEndpoints.export();

    const response = await get<Blob>(endpoint, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Import GL accounts
   */
  async import(file: File): Promise<BulkOperationResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await post<BulkOperationResponse>(
      sampleGLAccountEndpoints.import(),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Get GL accounts for dropdown/select
   */
  async getForDropdown(): Promise<SampleGLAccountListItem[]> {
    const response = await this.getPostingEnabled();
    return response.data;
  }

  /**
   * Get revenue accounts
   */
  async getRevenueAccounts(): Promise<ListResponse<SampleGLAccountListItem>> {
    return this.getByCategory('Revenue');
  }

  /**
   * Get expense accounts
   */
  async getExpenseAccounts(): Promise<ListResponse<SampleGLAccountListItem>> {
    return this.getByCategory('Expense');
  }
}

/**
 * Export singleton instance
 */
export const sampleGLAccountService = new SampleGLAccountService();
export default sampleGLAccountService;
